import {CSSProperties, FC, Key, ReactNode, useCallback, useEffect, useMemo, useState} from 'react';
import {Button, Drawer, Layout, Menu, theme} from 'antd';
import {MenuOutlined} from '@ant-design/icons';
import styled from 'styled-components';
import logo from "../assets/logo.png";
import aiIcon from "../assets/ai_icon.png";
import {Link, useLocation, useNavigate} from "react-router-dom";
import {colors} from "../util/colors.ts";
import {MenuItem} from "./menuItems.tsx";
import {User} from "../context/AuthContext.tsx";
import {User as ClientUser} from "client/src/context/AuthContext.tsx";
import {
    Briefcase,
    Calculator,
    ChalkboardTeacher,
    Envelope,
    Exam,
    Gear,
    Globe,
    GraduationCap,
    Headset,
    IdentificationCard,
    PenNib,
    UserCircle,
    ReadCvLogo
} from '@phosphor-icons/react';
import {useSubscriptionEndDate} from "../hooks/useSubscriptionEndDate";

const {Content, Sider, Header} = Layout;

const BOTTOM_MENU_KEYS = ['profile', 'account', 'hilfe-kontakt'] as const;

const isBottomMenuKey = (key: Key): key is "profile" | "account" =>
    typeof key === 'string' && BOTTOM_MENU_KEYS.includes(key as "profile" | "account");

type LayoutWrapperProps = {
    children: ReactNode;
    menuItems: MenuItem[];
    signOut: () => void;
    user?: User | ClientUser;
};

type MenuItemType = {
    key: Key;
    label: ReactNode;
    icon?: ReactNode;
    style?: CSSProperties;
    children?: MenuItemType[];
    type?: never;
    url?: string;
};
const AILabelWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;
const AIIconImage = styled.img`
    width: 16px;
    height: 16px;
    object-fit: contain;
`;

const MenuSectionHeader = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: #888;
    letter-spacing: 0.5px;
    padding-left: 4px;
`;
type MenuDividerType = {
    type: 'divider';
    key?: Key;
};

type CombinedMenuItemType = MenuItemType | MenuDividerType;

type IconKey =
    'pool'
    | 'profile'
    | 'account'
    | 'messages'
    | 'ai-jobs'
    | 'cv-generator'
    | 'anschreiben-generator'
    | 'online-kurse'
    | 'interview'
    | 'job-coach'
    | 'rentensteuerberater'
    | 'upgrade'
    | 'ai-job-web'
    | 'hilfe-kontakt';

const ICON_MAP: Record<IconKey, ReactNode | ''> = {
    'pool': <Briefcase size={18}/>,
    'profile': <UserCircle size={18}/>,
    'account': <Gear size={18}/>,
    'messages': <Envelope size={18}/>,
    'ai-jobs': <Globe size={18}/>,
    'cv-generator': <IdentificationCard size={18}/>,
    'anschreiben-generator': <PenNib size={18}/>,
    'online-kurse': <GraduationCap size={18}/>,
    'interview': <Exam size={18}/>,
    'job-coach': <ChalkboardTeacher size={18}/>,
    'rentensteuerberater': <Calculator size={18}/>,
    'hilfe-kontakt': <Headset size={18}/>,
    'ai-job-web': <ReadCvLogo size={18}/>,
    'upgrade': '',
};

const getIcon = (key: string): ReactNode => ICON_MAP[key as IconKey] ?? null;

const createAILabel = (text: string): ReactNode => (
    <AILabelWrapper>
        <span>{text}</span>
        <AIIconImage src={aiIcon as string} alt="AI"/>
    </AILabelWrapper>
);

const STATIC_MENU_ITEMS: MenuItemType[] = [
    {key: 'ai-job-web', label: <Link to="/ai-job-web">{createAILabel('Jobs aus dem Web')}</Link>, icon: getIcon('ai-job-web'), url: '/ai-job-web'},
    {key: 'ai-jobs', label: <Link to="/ai-jobs">{createAILabel('Jobsuche mit KI')}</Link>, icon: getIcon('ai-jobs'), url: '/ai-jobs'},
    {key: 'cv-generator', label: <Link to="/cv-generator">{createAILabel('Lebenslauf-Generator')}</Link>, icon: getIcon('cv-generator'), url: '/cv-generator'},
    {
        key: 'cover-letter',
        label: <Link to="/cover-letter">{createAILabel('Anschreiben-Generator')}</Link>,
        icon: getIcon('anschreiben-generator'),
        url: '/cover-letter'
    },
    {
        key: 'interview',
        label: <Link to="/interview">{createAILabel('Interviewvorbereitung')}</Link>,
        icon: getIcon('interview'),
        url: "/interview"
    },
    {key: 'job-coach', label: <Link to="/job-coach">{createAILabel('Job Coach')}</Link>, icon: getIcon('job-coach'), url: "/job-coach"},
    {
        key: 'rentensteuerberater',
        label: <Link to="/rentensteuerberater">{createAILabel('Steuer & Rente Experte')}</Link>,
        icon: getIcon('rentensteuerberater'),
        url: "/rentensteuerberater"
    },
    {
        key: 'courses',
        label: <Link to="/courses">{createAILabel('Online-Kurse')}</Link>,
        icon: getIcon('online-kurse'),
        url: "/courses"
    },
    // { key: 'upgrade', label: 'Silver+', url: '/silverplus', style: { color: '#8b5cf6' } },
];

export const LayoutWrapperCandidate: FC<LayoutWrapperProps> = ({children, menuItems, signOut}) => {
    const {token: {colorBgContainer, borderRadiusLG}} = theme.useToken();
    const location = useLocation();
    const navigate = useNavigate();
    const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
    const {endDate} = useSubscriptionEndDate();

    const currentPath = location.pathname.slice(1).split('/')[0] || 'pool';

    // Check if subscription is active (end date is valid and in the future)
    const hasActiveSubscription = endDate && !isNaN(endDate.getTime()) && endDate > new Date();

    const menuItemsWithIcons = useMemo(() =>
        menuItems.map(item => ({
            ...item,
            icon: getIcon(item.key as string),
        })), [menuItems]
    );

    const {topMenuItems, bottomMenuItems, allMenuItems} = useMemo(() => {
        const dynamicallyConstructedTopMenuItems: CombinedMenuItemType[] = [];

        menuItemsWithIcons.forEach(item => {
            if (!isBottomMenuKey(item.key)) {
                dynamicallyConstructedTopMenuItems.push(item as CombinedMenuItemType);
                if (item.key === 'messages') {
                    dynamicallyConstructedTopMenuItems.push({type: 'divider'});
                    // Add section header for AI features
                    dynamicallyConstructedTopMenuItems.push({
                        key: 'ai-features-header',
                        label: <MenuSectionHeader>silver+ Vorteile</MenuSectionHeader>,
                        style: {cursor: 'default', pointerEvents: 'none'}
                    } as MenuItemType);
                    STATIC_MENU_ITEMS.forEach(newItem => {
                        if (!isBottomMenuKey(newItem.key)) {
                            // Hide Silver+ menu item if user has active subscription
                            if (newItem.key === 'upgrade' && hasActiveSubscription) {
                                return;
                            }
                            dynamicallyConstructedTopMenuItems.push(newItem);
                        }
                    });
                }
            }
        });

        const bottomItems = menuItemsWithIcons.filter(item =>
            isBottomMenuKey(item.key)
        );

        // Combine all items together
        const combined = [...dynamicallyConstructedTopMenuItems, ...bottomItems];

        return {
            topMenuItems: dynamicallyConstructedTopMenuItems,
            bottomMenuItems: bottomItems,
            allMenuItems: combined
        };
    }, [menuItemsWithIcons, hasActiveSubscription]);

    const handleMenuClick = useCallback(({key}: { key: Key }) => {
        const item = [...topMenuItems, ...bottomMenuItems].find(item => item.key === key);
        if (item && 'url' in item && item.url) {
            navigate(item.url);
        }
    }, [topMenuItems, bottomMenuItems, navigate]);

    const handleMobileMenuClick = useCallback(({key}: { key: Key }) => {
        handleMenuClick({key});
        setMobileMenuVisible(false);
    }, [handleMenuClick]);

    const toggleMobileMenu = useCallback(() => {
        setMobileMenuVisible(prev => !prev);
    }, []);

    const handleSignOut = useCallback(() => {
        signOut();
        setMobileMenuVisible(false);
    }, [signOut]);

    useEffect(() => {
        setMobileMenuVisible(false);
    }, [location]);

    const menuProps = {
        theme: "light" as const,
        selectedKeys: [currentPath],
        style: {backgroundColor: colors.darkBg}
    };

    return (
        <Layout style={{minHeight: '100vh'}}>
            <MobileHeader style={{backgroundColor: colors.darkBg}}>
                <MobileHeaderContent>
                    <Link to="/">
                        <MobileLogo src={logo as string} alt="Silvertalent logo"/>
                    </Link>
                    <BurgerButton
                        icon={<MenuOutlined/>}
                        onClick={toggleMobileMenu}
                        style={{color: colors.text}}
                    />
                </MobileHeaderContent>
            </MobileHeader>

            <DesktopSider
                breakpoint="lg"
                collapsedWidth="0"
                theme="dark"
                width={250}
                style={{backgroundColor: colors.darkBg, height: "100vh"}}
            >
                <SidebarContent>
                    <LogoContainer>
                        <Link to="/">
                            <Logo src={logo as string} alt="Silvertalent logo"/>
                        </Link>
                    </LogoContainer>

                    <StyledMenu
                        {...menuProps}
                        mode="inline"
                        items={topMenuItems}
                        onClick={handleMenuClick}
                    />

                    {!hasActiveSubscription && <PromoWrapper>
                        <div style={{display: 'flex', alignItems: "center", gap: 4, marginBottom: 8}}>
                            <PromoTitle><img src={aiIcon} style={{width: 15, marginRight: 4}} alt=""/>Mehr Jobs mit silver+</PromoTitle>
                        </div>
                        <PromoDescription>Jetzt Zugang zu 100.000+ Rentnerjobs und KI Bewerbungshilfe sichern</PromoDescription>
                        <SilverButton onClick={()=>navigate("/silverplus")}>silver+ entdecken</SilverButton>
                    </PromoWrapper>}
                    <Spacer/>


                    {bottomMenuItems.length > 0 && (
                        <StyledMenu
                            {...menuProps}
                            mode="inline"
                            items={bottomMenuItems}
                        />
                    )}


                    <SignOutContainer>
                        <Button
                            onClick={signOut}
                            block
                            style={{borderColor: colors.bg, color: colors.text}}
                        >
                            Abmelden
                        </Button>
                    </SignOutContainer>
                </SidebarContent>
            </DesktopSider>

            <MobileOverlay
                placement="top"
                onClose={toggleMobileMenu}
                open={mobileMenuVisible}
                height="100%"
                styles={{
                    header: {display: 'none'},
                    body: {padding: 0, backgroundColor: colors.darkBg},
                    content: {backgroundColor: colors.darkBg}
                }}
                closeIcon={null}
                mask={false}
                maskClosable={false}
            >
                <MobileMenuContent>
                    <MobileMenuHeader>
                        <Link to="/" onClick={toggleMobileMenu}>
                            <MobileLogo src={logo as string} alt="Silvertalent logo"/>
                        </Link>
                        <Button
                            icon={<MenuOutlined/>}
                            onClick={toggleMobileMenu}
                            style={{color: colors.text, border: 'none', backgroundColor: 'transparent'}}
                        />
                    </MobileMenuHeader>

                    <StyledMenu
                        {...menuProps}
                        mode="vertical"
                        items={allMenuItems}
                        style={{
                            ...menuProps.style,
                            border: 'none',
                            fontSize: '18px'
                        }}
                        onClick={handleMobileMenuClick}
                    />

                    <MobileSignOutContainer>
                        <Button
                            onClick={handleSignOut}
                            block
                            size="large"
                            style={{borderColor: colors.bg, color: colors.text, fontSize: '18px'}}
                        >
                            Abmelden
                        </Button>
                    </MobileSignOutContainer>
                </MobileMenuContent>
            </MobileOverlay>

            <Layout style={{height: "100vh", overflowY: 'scroll'}}>
                <StyledContent style={{margin: '0', backgroundColor: colors.bg}}>
                    <ContentWrapper
                        style={{
                            padding: 0,
                            minHeight: 360,
                            background: colorBgContainer,
                            borderRadius: borderRadiusLG,
                        }}
                    >
                        {children}
                    </ContentWrapper>
                </StyledContent>
            </Layout>
        </Layout>
    );
};

const SidebarContent = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
`;

const StyledMenu = styled(Menu)`
    .ant-menu-item {
        display: flex !important;
        align-items: center;
    }
`;

const Spacer = styled.div`
    flex-grow: 1;
`;

const ContentWrapper = styled.div``;

const SignOutContainer = styled.div`
    padding: 16px;
`;

const LogoContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    border-bottom: 1px solid rgb(217 217 217);
`;

const Logo = styled.img`
    cursor: pointer;
    width: 150px;
`;

const StyledContent = styled(Content)`
    padding: 8px 24px 0;
    @media (max-width: 991px) {
        padding: 68px 8px 8px 8px;
        margin: 0;
    }
    @media (min-width: 992px) {
        @media (max-width: 767px) {
            padding: 0 8px;
        }
    }
`;

const MobileHeader = styled(Header)`
    display: none;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1000;
    height: 60px;
    line-height: 60px;
    padding: 0;

    @media (max-width: 991px) {
        display: block;
    }
`;

const MobileHeaderContent = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 16px;
    height: 100%;
`;

const MobileLogo = styled.img`
    height: 40px;
    width: auto;
`;

const PromoWrapper = styled.div`
    background: white;
    border-radius: 7px;
    padding: 8px;
    margin: 0 24px;
`;

const PromoTitle = styled.p`
    font-size: 12px;
    font-weight: bold;
    margin: 0;
`;

const PromoDescription = styled.p`
    margin: 0 0 8px 0;
    font-size: 12px;
`;

const BurgerButton = styled(Button)`
    border: none;
    background: transparent;
    font-size: 20px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background: transparent;
        color: ${colors.main};
    }
`;

const DesktopSider = styled(Sider)`
    @media (max-width: 991px) {
        display: none !important;
    }
`;

const MobileOverlay = styled(Drawer)`
    .ant-drawer-content {
        background-color: ${colors.darkBg} !important;
        transition: none !important;
        animation: none !important;
    }

    .ant-drawer-body {
        background-color: ${colors.darkBg} !important;
        height: 100vh;
        display: flex;
        flex-direction: column;
    }

    &.ant-drawer {
        .ant-drawer-content-wrapper {
            transition: none !important;
            animation: none !important;
        }

        .ant-drawer-content {
            transition: none !important;
            animation: none !important;
        }
    }
`;

const MobileMenuContent = styled.div`
    height: 100vh;
    display: flex;
    flex-direction: column;
    background-color: ${colors.darkBg};
`;

const MobileMenuHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    border-bottom: 1px solid rgb(217 217 217);
`;

const MobileSignOutContainer = styled.div`
    margin-top: auto;
    padding: 24px 16px;
`;
export const SilverButton= styled(Button)`
    background: linear-gradient(135deg, #CA6673 0%, #9177C7 50%, #4796E3 100%);
    color: white;
    border: none;
    box-shadow: none;

    &:hover:not(:disabled) {
        box-shadow: none;
        background: white !important;
        background-image: linear-gradient(white, white),
        linear-gradient(135deg, #CA6673 0%, #9177C7 50%, #4796E3 100%) !important;
        background-origin: border-box !important;
        background-clip: padding-box, border-box !important;
        border: 1px solid transparent !important;
        color: #CA6673 !important;
    }

    &:disabled {
        background: #d9d9d9;
        color: rgba(0, 0, 0, 0.25);
        cursor: not-allowed;
    }
`;
